import { FunctionComponent } from "react";
import ConnectedGenericView from "../../GenericView";
import { GenericView, GenericViewPlaceholder } from "../../GenericView/GenericView.web";
import EventViewHeaderPlaceholder from "../components/EventViewHeader/view/EventViewHeaderPlaceholder.web";
import { SCOREBOARD_SLOTS } from "../components/EventViewHeader/scoreboard-slots.web";
import { eventViewShellFor, excludedCardTypes, isAnyEventViewSportOn, type EventViewShell } from "../event-view-sports";
import { useEventViewActiveThrottles, useLatchedEventViewSportStatus } from "../model/EventView.graphql";
import { useEventViewVM } from "../viewmodel/EventView.viewmodel";
// Imported synchronously (RaceMeetingView parity): a lazy chunk flashed the placeholder on first
// paint even with a warm cache, since the header component wasn't loaded yet.
import EventViewHeader from "../components/EventViewHeader/view/EventViewHeader.web";

/**
 * Event View owner (RaceMeetingView pattern), pure Apollo — no Redux `connect`. Any registered throttle mounts
 * its Apollo path; a synchronously warmed sport fragment then picks the first-paint partition. None active →
 * Redux catalogue, no query. Never in `isApolloView` (load-bearing): `event-view-apollo-header-migration.md` §5.
 */
const EventView: FunctionComponent<{ urn: string }> = ({ urn }) => {
  const activeThrottles = useEventViewActiveThrottles();

  if (!isAnyEventViewSportOn(activeThrottles)) {
    return (
      // @ts-expect-error TODO Universal integration with placeholders still break on ts-jest
      <ConnectedGenericView urn={urn} component={GenericView} placeholder={GenericViewPlaceholder} root />
    );
  }

  return <ApolloEventViewGate urn={urn} activeThrottles={activeThrottles} />;
};

/**
 * Reads only the cache-warmed sport fragment, and fails CLOSED: anything but a resolved sport with a
 * registry entry whose throttle is active is the untouched catalogue. Reserving the slot on `unknown`
 * painted a placeholder over the catalogue's own switcher on every unwarmed navigation.
 */
const ApolloEventViewGate: FunctionComponent<{ urn: string; activeThrottles: ReadonlySet<string> }> = ({
  urn,
  activeThrottles,
}) => {
  const shell = eventViewShellFor(useLatchedEventViewSportStatus(urn), activeThrottles);

  if (shell) return <ApolloEventView urn={urn} shell={shell} />;

  return (
    // @ts-expect-error TODO Universal integration with placeholders still break on ts-jest
    <ConnectedGenericView urn={urn} component={GenericView} placeholder={GenericViewPlaceholder} root />
  );
};

/**
 * The sport is already established by the gate above, so this renders on `data` alone. Re-deriving it
 * from the fragment let a partial write regress a painted switcher to its placeholder.
 */
const ApolloEventView: FunctionComponent<{ urn: string; shell: EventViewShell }> = ({
  urn,
  shell: { sportId, entry },
}) => {
  const {
    error,
    vm: { data },
  } = useEventViewVM(urn);
  const slot = entry.scoreboard ? SCOREBOARD_SLOTS[sportId] : undefined;

  // Fail open: if the header query errors with only the warm placeholder (or nothing) to show, render
  // the untouched catalogue rather than strip its switcher behind a dead one — web has no refresh to recover.
  if (error && (!data || data.warmOnly)) {
    return (
      // @ts-expect-error TODO Universal integration with placeholders still break on ts-jest
      <ConnectedGenericView urn={urn} component={GenericView} placeholder={GenericViewPlaceholder} root />
    );
  }

  return (
    <>
      {data ? <EventViewHeader data={data} slot={slot} /> : <EventViewHeaderPlaceholder slot={slot} />}
      <ConnectedGenericView
        urn={urn}
        // @ts-expect-error TODO Universal integration with placeholders still break on ts-jest
        component={GenericView}
        placeholder={GenericViewPlaceholder}
        excludeCardTypes={excludedCardTypes(entry)}
        // The switcher is an external header above this body, so the body's first card must keep
        // its themed top gap (GenericView.web.css).
        hasExternalHeader
        // Web navigates on selection (`event-view-apollo-header-migration.md` §13.4.8), so this carries the body
        // over faded while the route re-renders with the new urn, rather than dropping to a placeholder.
        keepPreviousState
        root
      />
    </>
  );
};

export default EventView;
